import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { WorkOrderDocumentService } from '@app/work-order/documents/work-order-document.service';
import { OfflineDataDb } from '@app/core/db/offline-data';
import {
  LoadWorkOrder,
  OpenMarkupDocument,
  LoadMarkups,
  SaveMarkups,
  SaveMarkupsSuccess,
  SaveMarkupsFailure,
  DeleteMarkup,
  ClearMarkupsState,
  PdfMarkupActionTypes,
} from '@dwo/actions';
import { State } from '@dwo/reducers';
import { getMarkupStateForDocument } from '@app/core/store/selectors/pdf-markup.selector';
import { DocumentDto } from '@app/generated/model';
import { DocumentControllerService } from '@app/generated/service';
import { env } from '../../../environments/env';
import * as pdfjsLib from 'pdfjs-dist';
import type { PageViewport } from 'pdfjs-dist';
import type { RedlineTool } from './pdf-redline/pdf-redline.component';
import { PdfRedlineComponent } from './pdf-redline/pdf-redline.component';
import type { ViewportReadyEvent } from './pdf-canvas/pdf-canvas.component';
import type { AnnotationsByPage } from './pdf-markup-annotation.model';
pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdfjs/build/pdf.worker.min.mjs';

interface WebkitFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?(): Promise<void> | void;
}
interface WebkitFullscreenDocument extends Document {
  webkitExitFullscreen?(): Promise<void> | void;
  webkitFullscreenElement?: Element | null;
}

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 1.0;
const ZOOM_MAX = 4.0;
const SAVE_DEBOUNCE_MS = 800;

@Component({
  selector: 'app-pdf-markup',
  templateUrl: './pdf-markup.component.html',
  styleUrls: ['./pdf-markup.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PdfMarkupComponent implements OnInit, OnDestroy {
  @ViewChild(PdfRedlineComponent) redline?: PdfRedlineComponent;
  @ViewChild('pdfMarkupWrapper') pdfMarkupWrapperRef?: ElementRef<HTMLElement>;

  loading = true;
  isFullscreen = false;
  errorMessage: string | null = null;
  currentPage = 1;
  totalPages = 0;
  scale = 1.5;
  rotation = 0;
  canUndo = false;
  canRedo = false;
  isUndoRedoBusy = false;
  isDownloading = false;
  isSendingToMips = false;

  redlineActiveTool: RedlineTool = '';
  lineWidth = 1;
  strokeColor = '#ff0000';
  fillColor = 'transparent';
  activeObjectType: string | null = null;

  pdfDoc: pdfjsLib.PDFDocumentProxy | null = null;
  activeViewport: PageViewport | null = null;
  baseWidth = 0;
  baseHeight = 0;

  documentId = '';
  initialAnnotations: AnnotationsByPage = {};
  private workOrderId = '';
  private currentDocument: DocumentDto | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed$ = new Subject<void>();
  private docSwitch$ = new Subject<void>();
  private savedAnnotationIds = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private offlineData: OfflineDataDb,
    private workOrderDocumentService: WorkOrderDocumentService,
    private httpClient: HttpClient,
    private store: Store<State>,
    private cdr: ChangeDetectorRef,
    private documentController: DocumentControllerService,
    private ngrxActions: Actions,
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((params) => params.get('document-id') ?? ''),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe((documentId) => {
        void this.loadDocument(documentId);
      });
  }

  private async loadDocument(documentId: string): Promise<void> {
    this.docSwitch$.next();
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      if (this.documentId) this.persistAnnotations();
    }

    if (this.documentId && this.documentId !== documentId) {
      this.store.dispatch(new ClearMarkupsState(this.documentId));
    }

    const workOrderId = this.route.parent?.parent?.snapshot.paramMap.get('id') ?? '';
    this.workOrderId = workOrderId;
    this.documentId = documentId;
    this.loading = true;
    this.errorMessage = null;
    this.pdfDoc = null;
    this.activeViewport = null;
    this.baseWidth = 0;
    this.baseHeight = 0;
    this.currentPage = 1;
    this.initialAnnotations = {};

    this.store.dispatch(new OpenMarkupDocument(documentId));
    this.store.dispatch(new LoadWorkOrder(workOrderId));

    const document = await this.resolveDocument(workOrderId, documentId);
    if (!document?.link) {
      this.errorMessage = 'Document not found.';
      this.loading = false;
      return;
    }
    this.currentDocument = document;

    const url = document.link.toString();
    const storedFiles = await this.offlineData.workOrderFiles.where('workOrderId').equals(workOrderId).toArray();
    const offline = storedFiles.find((f) => f.url === url);

    let data: Uint8Array | null = null;
    if (offline) {
      data = new Uint8Array(offline.data);
    } else {
      const blob = await this.httpClient
        .get(url, { responseType: 'blob' })
        .toPromise()
        .catch(() => null);
      data = blob ? new Uint8Array(await blob.arrayBuffer()) : null;
    }

    if (!data) {
      this.errorMessage = 'Could not load document.';
      this.loading = false;
      return;
    }

    try {
      const doc = await pdfjsLib.getDocument({ data }).promise;
      this.totalPages = doc.numPages;
      this.pdfDoc = doc;
      this.store.dispatch(new LoadMarkups(documentId));
      this.store
        .select(getMarkupStateForDocument(documentId))
        .pipe(
          filter((s) => s.loaded || !!s.error),
          take(1),
          takeUntil(this.docSwitch$),
          takeUntil(this.destroyed$),
        )
        .subscribe((s) => {
          if (s.error) {
            this.errorMessage = 'Failed to load saved annotations.';
          } else {
            this.initialAnnotations = s.annotationsByPage;
            this.savedAnnotationIds = new Set(
              Object.values(s.annotationsByPage)
                .flat()
                .map((a) => a.id)
                .filter((id): id is string => !!id),
            );
          }
          this.cdr.detectChanges();
        });
      this.loading = false;
      this.cdr.detectChanges();
    } catch (err) {
      console.error('[PdfMarkup] Failed to load PDF:', err);
      this.errorMessage = 'Failed to load PDF.';
      this.loading = false;
    }
  }

  onAnnotationsChanged(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.persistAnnotations(), SAVE_DEBOUNCE_MS);
  }

  private persistAnnotations(): void {
    if (!this.redline || !this.documentId) return;
    const byPage = this.redline.getAllAnnotationsByPage();
    this.savedAnnotationIds = new Set(
      Object.values(byPage)
        .flat()
        .map((a) => a.id)
        .filter((id): id is string => !!id),
    );
    this.store.dispatch(new SaveMarkups(this.documentId, byPage));
  }

  onUndoRedoApplied(): void {
    if (!this.redline || !this.documentId) return;

    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }

    const byPage = this.redline.getAllAnnotationsByPage();
    const currentIds = new Set(
      Object.values(byPage)
        .flat()
        .map((a) => a.id)
        .filter((id): id is string => !!id),
    );

    for (const id of this.savedAnnotationIds) {
      if (!currentIds.has(id)) {
        this.store.dispatch(new DeleteMarkup(this.documentId, id));
      }
    }

    this.savedAnnotationIds = currentIds;
    this.store.dispatch(new SaveMarkups(this.documentId, byPage));
  }

  onAnnotationDeleted(markupId: string): void {
    if (!this.documentId || !markupId) return;
    this.savedAnnotationIds.delete(markupId);
    this.store.dispatch(new DeleteMarkup(this.documentId, markupId));
  }

  onPageChange(page: number): void {
    this.currentPage = page;
  }

  onViewportReady(evt: ViewportReadyEvent): void {
    this.activeViewport = evt.viewport;
    this.baseWidth = evt.baseWidth;
    this.baseHeight = evt.baseHeight;
  }

  onRedlineToolChange(tool: RedlineTool): void {
    this.redlineActiveTool = tool;
    this.redline?.setTool(tool);
  }

  onToolDeactivated(): void {
    this.redlineActiveTool = '';
  }

  onActiveObjectTypeChange(type: string | null): void {
    this.activeObjectType = type;
  }

  onLineWidthChange(w: number): void {
    this.lineWidth = w;
  }

  onZoomIn(): void {
    this.scale = Math.min(this.scale + ZOOM_STEP, ZOOM_MAX);
  }
  onZoomOut(): void {
    this.scale = Math.max(this.scale - ZOOM_STEP, ZOOM_MIN);
  }
  onRotate(): void {
    this.rotation = (this.rotation + 90) % 360;
  }

  onClearActive(): void {
    this.redline?.clearActiveObject();
  }
  onClearAll(): void {
    this.redline?.clearAll();
  }

  onHistoryChanged(state: { canUndo: boolean; canRedo: boolean }): void {
    this.canUndo = state.canUndo;
    this.canRedo = state.canRedo;
    this.cdr.detectChanges();
  }

  async onUndo(): Promise<void> {
    if (this.isUndoRedoBusy || !this.canUndo) return;
    this.isUndoRedoBusy = true;
    this.cdr.detectChanges();
    try {
      await this.redline?.undo();
    } finally {
      this.isUndoRedoBusy = false;
      this.cdr.detectChanges();
    }
  }

  async onRedo(): Promise<void> {
    if (this.isUndoRedoBusy || !this.canRedo) return;
    this.isUndoRedoBusy = true;
    this.cdr.detectChanges();
    try {
      await this.redline?.redo();
    } finally {
      this.isUndoRedoBusy = false;
      this.cdr.detectChanges();
    }
  }

  async onDownload(): Promise<void> {
    if (!this.workOrderId || !this.documentId || this.isDownloading) return;
    this.isDownloading = true;
    this.cdr.detectChanges();
    try {
      await this.flushPendingSave();

      const response = (await firstValueFrom(
        this.documentController.getAttachmentWithMarkups(this.workOrderId, this.documentId, 'response', false, {
          httpHeaderAccept: '*/*' as unknown as 'application/json',
        }),
      )) as unknown as HttpResponse<Blob>;

      const blob = response.body ?? new Blob([], { type: 'application/pdf' });
      const fileName =
        this.extractFileName(response.headers?.get('Content-Disposition')) || this.buildFallbackFileName();

      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.style.display = 'none';
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      console.error('[PdfMarkup] Failed to download markup document:', err);
    } finally {
      this.isDownloading = false;
      this.cdr.detectChanges();
    }
  }

  async onSendToMips(): Promise<void> {
    if (!this.workOrderId || !this.documentId || this.isSendingToMips) return;
    this.isSendingToMips = true;
    this.cdr.detectChanges();
    try {
      await this.flushPendingSave();

      const encodedWorkOrderId = encodeURIComponent(this.workOrderId);
      const encodedDocumentId = encodeURIComponent(this.documentId);
      await firstValueFrom(
        this.httpClient.post<void>(
          `${env.get().api.url}/work-order/${encodedWorkOrderId}/document/${encodedDocumentId}/mips`,
          null,
        ),
      );
    } catch (err) {
      console.error('[PdfMarkup] Failed to send markup document to MIPS:', err);
    } finally {
      this.isSendingToMips = false;
      this.cdr.detectChanges();
    }
  }

  private extractFileName(contentDisposition: string | null | undefined): string | null {
    if (!contentDisposition) return null;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private buildFallbackFileName(): string {
    const base = this.currentDocument?.filename || this.currentDocument?.name || 'document.pdf';
    const dot = base.lastIndexOf('.');
    return dot > 0 ? `${base.slice(0, dot)}-markup${base.slice(dot)}` : `${base}-markup.pdf`;
  }

  private async flushPendingSave(): Promise<void> {
    if (!this.saveTimer) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    this.persistAnnotations();
    await firstValueFrom(
      this.ngrxActions.pipe(
        ofType<SaveMarkupsSuccess | SaveMarkupsFailure>(
          PdfMarkupActionTypes.SaveMarkupsSuccess,
          PdfMarkupActionTypes.SaveMarkupsFailure,
        ),
        filter((action) => action.documentId === this.documentId),
        take(1),
      ),
    ).catch(() => undefined);
  }

  private get fullscreenElement(): Element | null {
    const doc = document as WebkitFullscreenDocument;
    return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
  }

  async toggleFullscreen(): Promise<void> {
    const doc = document as WebkitFullscreenDocument;
    if (this.isFullscreen) {
      this.isFullscreen = false;
      if (this.fullscreenElement) {
        try {
          await (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
        } catch (err) {
          console.warn('Failed to exit fullscreen', err);
        }
      }
      return;
    }

    this.isFullscreen = true;
    const el = this.pdfMarkupWrapperRef?.nativeElement as WebkitFullscreenElement | undefined;
    if (el) {
      try {
        await (el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.());
      } catch (err) {
        console.warn('Native fullscreen unavailable, using CSS-only fallback', err);
      }
    }
  }

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreen = !!this.fullscreenElement;
    this.cdr.detectChanges();
  }

  private async resolveDocument(workOrderId: string, documentId: string): Promise<DocumentDto | null> {
    const inMemory = this.workOrderDocumentService.get();
    if (Array.isArray(inMemory) && inMemory.length > 0) {
      return inMemory.find((d) => d.id === documentId) ?? null;
    }

    this.workOrderDocumentService.initialize(workOrderId);
    const docs = await firstValueFrom(
      this.workOrderDocumentService.getData().pipe(
        filter((d): d is DocumentDto[] => Array.isArray(d)),
        take(1),
      ),
    ).catch(() => null as DocumentDto[] | null);

    return Array.isArray(docs) ? (docs.find((d) => d.id === documentId) ?? null) : null;
  }

  ngOnDestroy(): void {
    this.isFullscreen = false;
    if (this.fullscreenElement) {
      const doc = document as WebkitFullscreenDocument;
      void (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
    }
    this.destroyed$.next();
    this.destroyed$.complete();
    this.docSwitch$.next();
    this.docSwitch$.complete();
    this.pdfDoc?.cleanup();
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      this.persistAnnotations();
    }
    if (this.documentId) {
      this.store.dispatch(new ClearMarkupsState(this.documentId));
    }
  }
}
